import { ApolloError } from "@apollo/client";
import { Mutation, UseCases } from "@aptus/frontend-core";
import event from "event";
import { t } from "i18next";
import { InternalAPI } from "models/internalAPI";
import { useState } from "react";
import { Country, Success } from "./models/country";
import { CreateCountryInput, DeleteCountryInput, SaveCountryInput, UpdateCountryInput } from "./models/countryInput";

export type CountriesAPI = InternalAPI<Country[]>;

export type UpdateCountryMutation = Mutation<UpdateCountryInput, Country>;
export type CreateCountryMutation = Mutation<CreateCountryInput, Country>;
export type DeleteCountryMutation = Mutation<DeleteCountryInput, Success>;

interface Props {
	API: CountriesAPI;
	defaultCountry: Country;
	updateMutation: UpdateCountryMutation;
	createMutation: CreateCountryMutation;
	deleteMutation: DeleteCountryMutation;
}
interface Result {
	country: Country;
	countries: Country[];
	isLoading: boolean;
	error?: Error;
	save: (input: SaveCountryInput) => Promise<void>;
	remove: (id: string) => Promise<void>;
}

export const useCountriesUseCases: UseCases<Props, Result> = ({ API, defaultCountry, updateMutation, createMutation, deleteMutation }) => {
	const [isLoading, setIsLoading] = useState<Result["isLoading"]>(false);
	const isUpdate = (input: SaveCountryInput): input is UpdateCountryInput => !!input.id;

	const save: Result["save"] = async (input) => {
		setIsLoading(true);
		try {
			if (isUpdate(input)) {
				await updateMutation(input);
				event.emit("mutationSucceeded", t("countryHasBeenSuccessfullyUpdated"));
			} else {
				await createMutation(input);
				event.emit("mutationSucceeded", t("countryHasBeenSuccessfullyCreated"));
			}
		} catch (error) {
			if (error instanceof ApolloError) {
				event.emit("mutationFailed", error);
			}
		}
		setIsLoading(false);
	};

	const remove = async (id: string) => {
		try {
			await deleteMutation({ id });
			event.emit("mutationSucceeded", t("countryHasBeenSuccessfullyDeleted"));
		} catch (error) {
			if (error instanceof ApolloError) {
				event.emit("mutationFailed", error);
			}
		}
	};

	return {
		country: API.data[0] || defaultCountry,
		countries: API.data || [],
		isLoading: API.isLoading || isLoading,
		save,
		remove,
	};
};
